import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { ArticleCardSm } from 'components/pages/insights/article-card-sm';
import { CARD_GAP } from 'components/pages/insights/constants';
import { EmblaScrollContainer } from 'components/pages/insights/embla-scroll-container';
import { SectionHeader } from 'components/pages/insights/section-header';
import { useGetMostLikedArticles } from 'hooks/insights/use-get-most-liked-articles';
import { memo, useCallback } from 'react';
import { useAnalytics } from 'services/analytics';

export const RECOMMENDED_CATEGORY_ID = 'recommended';

type TRecommendationsSectionProps = {
  excludeArticleIds: string[];
  profile: string;
};

export const RecommendationsSection = memo(({ excludeArticleIds, profile }: TRecommendationsSectionProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  const handleUpNextClick = useCallback(
    (nextArticleId: string | number) => {
      trackEvent('insights_article_up_next_tapped', {
        article_id: excludeArticleIds[0] || '',
        next_article_id: nextArticleId,
      });
    },
    [trackEvent, excludeArticleIds],
  );

  const { data: mostLikedArticles } = useGetMostLikedArticles({
    count: 6,
    excludeArticleIds,
    profile,
  });

  const handleSeeAll = (): void => {
    void navigate({
      to: '/insights/$categoryId',
      // @ts-expect-error strictNullChecks breaks TanStack Router inference
      params: { categoryId: RECOMMENDED_CATEGORY_ID },
      // @ts-expect-error strictNullChecks breaks TanStack Router inference
      search: { type: 'articles' as const },
    });
  };

  if (mostLikedArticles.length === 0) return null;

  if (isMobile) {
    return (
      <Box>
        <Box sx={{ mb: 3 }}>
          <SectionHeader title="You might also like" onSeeAll={handleSeeAll} />
        </Box>
        <EmblaScrollContainer gap={2} itemSx={{ flex: '0 0 168px', minWidth: 0 }}>
          {mostLikedArticles.map((article) => (
            <Box key={article.id}>
              <ArticleCardSm
                articleId={article.id}
                image={article.coverSrc}
                title={article.title}
                isNew={article.isNew}
                imageHeight={200}
                source="up_next"
                onClick={() => handleUpNextClick(article.id)}
              />
            </Box>
          ))}
        </EmblaScrollContainer>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <SectionHeader title="You might also like" onSeeAll={handleSeeAll} />
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: CARD_GAP }}>
        {mostLikedArticles.slice(0, 4).map((article) => (
          <ArticleCardSm
            key={article.id}
            articleId={article.id}
            image={article.coverSrc}
            title={article.title}
            isNew={article.isNew}
            imageHeight={228}
            source="up_next"
            onClick={() => handleUpNextClick(article.id)}
          />
        ))}
      </Box>
    </Box>
  );
});
